<script setup lang="ts">
// Gold's (i) explainer prose for the shared FundSignalPanel — moved verbatim
// out of GoldSignalPanel.vue (option C, 2026-10-11). Rendered inside the
// panel's .explainer box, which owns the styling.
import { computed } from 'vue'
import { bps, type FundSignalView } from './fundSignalPanel'
import { goldGateFields, gateStateText } from './fundSignalFields'
import { formatOverlayPct } from '../../utils/goldOverlay'

const props = defineProps<{ section: string; v: FundSignalView }>()

const gate = computed(() => goldGateFields(props.v.signal))

const mVerdictText = computed(() => {
  const { mBps, thresholdBps } = props.v
  if (mBps == null || thresholdBps == null) return null
  return mBps >= thresholdBps
    ? `at/above the ${thresholdBps.toFixed(1)} bps threshold — momentum leg passes`
    : `below the ${thresholdBps.toFixed(1)} bps threshold — no BUY today from momentum`
})

const dyVerdictText = computed(() => {
  const { dyBps, dropBps } = gate.value
  if (dyBps == null || dropBps == null) return null
  return dyBps < -dropBps
    ? `beyond the −${dropBps.toFixed(0)} bps collapse trigger — gate closes`
    : `inside the −${dropBps.toFixed(0)} bps collapse trigger — no yield collapse, gate unaffected`
})

// Live gold overlay bridge read-outs (deriveGoldOverlay via the panel).
const ov = computed(() => {
  const o = props.v.overlay
  const barInfo = (() => {
    if (o?.barTimeUtc == null) return null
    const d = new Date(o.barTimeUtc)
    const timeStr = Number.isNaN(d.getTime()) ? o.barTimeUtc : `${d.toISOString().slice(11, 16)}Z`
    const age = o.barAgeMinutes
    return age != null ? `bar ${timeStr}, ${age.toFixed(0)}m old` : `bar ${timeStr}`
  })()
  return {
    baseKt: formatOverlayPct(props.v.ktLatest?.returnFrac != null ? props.v.ktLatest.returnFrac : null),
    goldMove: formatOverlayPct(o?.goldMoveFrac ?? null),
    beta: o?.beta != null ? o.beta.toFixed(2) : '—',
    frac: formatOverlayPct(o?.overlayFrac ?? null),
    combined: formatOverlayPct(o?.combinedFrac ?? null),
    barInfo,
  }
})
</script>

<template>
  <section v-if="section === 'm1040'">
    <p class="info-now"><strong>Now:</strong>
      m<sub>10:40</sub> = <code>{{ bps(v.mBps) }}</code><span v-if="mVerdictText">, {{ mVerdictText }}</span>.
      <span v-if="v.thresholdReasonText">Threshold: {{ v.thresholdReasonText }}.</span>
    </p>
    <p><strong>m<sub>10:40</sub> — GC momentum into the decision</strong></p>
    <p>The return of the GC gold-futures front month from the prior session's
      16:00 America/New York close (walking back up to 5 sessions across
      holidays) to the last print before the 08:40 London decision.
      <strong>BUY requires m ≥ threshold.</strong> The threshold echoes
      reentry hysteresis: a steady value while invested, a higher re-entry
      bar while flat (position proxy). A GC print older than the staleness
      cap degrades the day to HOLD rather than trading on old data.</p>
  </section>
  <section v-else-if="section === 'dy20'">
    <p class="info-now"><strong>Now:</strong>
      Δy<sub>20</sub> = <code>{{ bps(gate.dyBps) }}</code><span v-if="dyVerdictText">, {{ dyVerdictText }}</span>.
    </p>
    <p><strong>Δy<sub>20</sub> — 10-year Treasury yield change</strong></p>
    <p>The change in the 10y US Treasury yield over the last 20 rows of the
      yields calendar (in bps). It feeds the yield-collapse gate: when
      Δy<sub>20</sub> falls below <code>−{{ gate.dropBps != null ? gate.dropBps.toFixed(0) : '—' }} bps</code>
      the gate closes and blocks BUY.</p>
    <p><strong>Why block when yields collapse, if falling yields are good for
      gold?</strong> That intuition is about bullion — lower real rates
      reduce the metal's opportunity cost, and ordinary falling-yield
      regimes DO help. But a collapse this violent (&gt;{{ gate.dropBps != null ? gate.dropBps.toFixed(0) : '50' }} bps
      in 20 sessions — 72 days in 16 backtest years) signals crisis
      deleveraging (March-2020 archetype), and this fund holds gold
      <em>mining equities</em>, which crash with the equity market in those
      episodes regardless of what bullion does. The gate steps aside for
      that tail only; positive or mildly negative Δy<sub>20</sub> leaves it
      untouched. Evidence: Sharpe 2.46 → 2.58 and MaxDD 44.3% → 20.7% at
      ~zero CAGR cost (2026-07-25 treasury-overlay arc, C# engine A/B).</p>
  </section>
  <section v-else-if="section === 'gate'">
    <p class="info-now"><strong>Now:</strong>
      gate is <code>{{ gateStateText(gate.gateOpen) }}</code>
      (mode <code>{{ gate.gateMode ?? '—' }}</code>)<span v-if="gate.gateReasonText">: {{ gate.gateReasonText }}</span>.
    </p>
    <p><strong>Yield gate — the only veto on a momentum BUY</strong></p>
    <p>With mode <code>on</code>, a BUY from momentum is vetoed when
      Δy<sub>20</sub> breaches the drop trigger. With mode <code>off</code>
      it never blocks. If the yields file is missing or too short the gate
      <strong>fails OPEN</strong> (marked degraded) — matching the C#
      backtest engine's convention, so live and backtest disagree nowhere.</p>
  </section>
  <section v-else-if="section === 'decision'">
    <p class="info-now"><strong>Now:</strong>
      <code>{{ v.decisionLabel }}</code><span v-if="v.decisionReasonText"> — {{ v.decisionReasonText }}</span>.
    </p>
    <p><strong>Decision — published once per UK business day</strong></p>
    <p>The producer fires at <strong>08:40 Europe/London</strong> (mirrors
      BGA/JH's decision instant), 20 minutes ahead of the <strong>09:00
      London HL dealing cutoff</strong> for the Ninety One fund.
      <code>BUY</code> = momentum ≥ threshold AND gate open; anything else
      is <code>SELL</code> (full liquidation per the dealing-plan canon).
      A degraded day publishes <code>HOLD</code> and carries the prior
      position forward via the position proxy — never a fresh trade off
      stale data.</p>
  </section>
  <section v-else-if="section === 'overlay'">
    <p class="info-now"><strong>Now:</strong>
      base K<sub>t</sub> <code>{{ ov.baseKt }}</code>,
      gold since NY anchor <code>{{ ov.goldMove }}</code><span v-if="ov.barInfo"> ({{ ov.barInfo }})</span>,
      β (3Y refit) <code>{{ ov.beta }}</code>,
      overlay <code>{{ ov.frac }}</code>,
      combined <code>{{ ov.combined }}</code> — combined ← P&amp;L uses this.
    </p>
    <p><strong>Live gold overlay — bridging K_t to the P&amp;L between daily closes</strong></p>
    <p>The base K<sub>t</sub> synthetic NAV is a daily-close estimate, frozen until the
      next publish. Between closes, the overlay layers β·Δgold — the GC gold-futures
      move since the prior session's 16:00 America/New York close, scaled by the fund's
      trailing 3-year beta to gold — on top of that frozen base, so the signal page can
      show the same live-tracking number the P&amp;L bridge already uses.</p>
    <p>It appears only in overlay-eligible windows: pre-07:35 UK (ahead of the next
      base K<sub>t</sub> publish), weekends/holidays, and restored payloads. The base
      K<sub>t</sub> figure above is never touched by the overlay — it stays the
      unmodified daily-close estimate at all times.</p>
  </section>
</template>
